import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {useDispatch, useSelector} from 'react-redux';

import {
  selectApplicantInformation,
  selectUserInfo,
  selectHasCrmPermission,
} from 'src/selectors/crm';

import {useInflight} from 'src/hooks/redux';

import {
  getApplicantInformation,
  updateCrmApplicantInformationData,
} from 'src/action-creators/crm';

import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';

import {
  SubTitleSmall,
  ButtonTextSmall,
  BodyMedium,
  SubTitleLarge,
  BodyLarge,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';

import {formatMomentDateWithFormat} from 'src/utils/date-time';

import AttachFile from './attach-file';

import css from './style.css';

//Move to util later
const getApiPrefix = (role: string) => {
  return role === 'AGENCY' ? 'trmapi/agency' : '/trmapi';
};

const ApplicantAttachments = ({
  jobId,
  applicantId,
  hideAttachmentsApplicationSpecificAttachments = false,
  showActionButtons = true,
}: {
  jobId: number;
  applicantId: number;
  hideAttachmentsApplicationSpecificAttachments?: boolean;
  showActionButtons?: boolean;
}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserInfo);
  const viewCandidateAttachments = selectHasCrmPermission(
    userDetails,
    CRM_PERMISSIONS.CRUD_ATTACHMENT,
  );
  const [showAttachFileModal, setShowAttachFileModal] = React.useState(false);
  const [attachmentType, setAttachmentType] = React.useState('CANDIDATE');
  const [editFileName, setEditFileName] = React.useState('');
  const [editAttachmentType, setEditAttachmentType] = React.useState('');
  const [editFileId, setEditFileId] = React.useState(0);
  const [isEdit, setIsEdit] = React.useState(false);
  const applicantDetails = useSelector(selectApplicantInformation);

  const candidateAttachments = [
    {
      label: t('Contact_details', 'Contact details'),
      value: 'CONTACT',
    },
    {label: t('Resume', 'Resume'), value: 'RESUME'},
    {
      label: t('Work_experience', 'Work experience'),
      value: 'WORK_EXP_DOCUMENTS',
    },
    {
      label: t('Address_proof', 'Address proof'),
      value: 'ADDRESS_PROOF',
    },
    {
      label: t('Photo_ID_proof', 'Photo ID proof'),
      value: 'PHOTO_ID_PROOF',
    },
    {
      label: t('Educational_documents', 'Educational documents'),
      value: 'EDUCATION_DOCUMENTS',
    },
    {label: t('Others', 'Others'), value: 'OTHER'},
    {label: t('Candidate_photo', 'Candidate photo'), value: 'CANDIDATE_PHOTO'},
  ];

  const applicationAttachments = [
    {label: t('Resume', 'Resume'), value: 'RESUME'},
    {label: t('Review', 'Review'), value: 'REVIEW'},
    {label: t('Offer', 'Offer'), value: 'OFFER'},
    {label: t('Others', 'Others'), value: 'OTHER'},
  ];

  const isApplicantsLoading = useInflight(
    getApplicantInformation(applicantId, jobId.toString()),
  );

  const {organization: organizationInfo, user: userInfo} =
    useSelector(selectUserInfo) ?? {};

  React.useEffect(() => {
    if (!applicantDetails && Boolean(applicantId) && Boolean(jobId)) {
      dispatch(getApplicantInformation(applicantId, jobId.toString()));
    }
  }, []);

  if (isApplicantsLoading) {
    return <CircularLoader />;
  }

  if (!applicantDetails) {
    return null;
  }

  // @ts-expect-error - TS18048 - 'applicantDetails.applications' is possibly 'undefined'.
  const currentApplication = applicantDetails.applications.find(
    (app) => app.jobId === jobId,
  );

  const attachments = applicantDetails.candidateAttachments;

  const {integrationType = 'NONE'} = organizationInfo;

  const apiPrefix = getApiPrefix(userInfo?.roleTitle ?? '');

  const attachmentTableHeader = [
    {
      key: 'fileName',
      label: t('File_Name', 'File Name'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.nameColumn}>
            <div className={css.flex}>
              <SubTitleSmall>
                <Link
                  href={`${apiPrefix}/api/attachments/${data.id}/download`}
                  target="_blank"
                  underline={false}
                >
                  <TruncatedTextWithTooltip>
                    {data.filename ?? '-'}
                  </TruncatedTextWithTooltip>
                </Link>
              </SubTitleSmall>
              {integrationType === 'TALEO' ? (
                <Tooltip
                  title={
                    data.taleoSynced
                      ? t('Synced_with_Taleo', 'Synced with Taleo')
                      : t('Not_Synced_with_Taleo', 'Not Synced with Taleo')
                  }
                  elevation="modal"
                >
                  <Icon
                    color={data.taleoSynced ? 'success' : 'danger'}
                    name={
                      data.taleoSynced ? 'check-circle' : 'exclamation-circle'
                    }
                  />
                </Tooltip>
              ) : null}
            </div>
          </BasicSingleCell>
        );
      },
    },
    {
      key: 'attachmentType',
      label: t('Type', 'Type'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => {
        const attachment = [
          ...candidateAttachments,
          ...applicationAttachments,
        ].find((attach) => attach.value === data.attachmentType);
        return (
          <BasicSingleCell className={css.attachmentTypeColumn}>
            <BodyMedium>
              <TruncatedTextWithTooltip>
                {attachment ? attachment.label : '-'}
              </TruncatedTextWithTooltip>
            </BodyMedium>
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
    {
      key: 'updatedBy',
      label: t('Updated_by', 'Updated by'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.updatedByColumn}>
            <BodyMedium>
              <TruncatedTextWithTooltip>
                {data.updatedByName || '-'}
              </TruncatedTextWithTooltip>
            </BodyMedium>
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
    {
      key: 'updatedOn',
      label: t('Updated_on', 'Updated on'),
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.updatedOnColumn}>
            <BodyMedium>
              <TruncatedTextWithTooltip>
                {formatMomentDateWithFormat(data.updatedOn)}
              </TruncatedTextWithTooltip>
            </BodyMedium>
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
    {
      key: 'actions',
      label: '',
      // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
      render: ({data}) => {
        return (
          <BasicSingleCell className={css.flex}>
            {viewCandidateAttachments && showActionButtons && (
              <Button
                iconLeftName="pen"
                type="ghost"
                onClick={() => {
                  setShowAttachFileModal(true);
                  setAttachmentType('CANDIDATE');
                  setEditFileName(data.filename);
                  setEditAttachmentType(data.attachmentType);
                  setEditFileId(data.id);
                  setIsEdit(true);
                }}
                size="small"
              />
            )}
          </BasicSingleCell>
        );
      },
      sortable: false,
    },
  ];

  if (!applicantDetails) {
    return null;
  }
  return (
    <div className={css.attachmentsContainer}>
      {currentApplication && !hideAttachmentsApplicationSpecificAttachments ? (
        <Card
          classNames={{
            wrapper: css.card,
          }}
        >
          <CardHeader>
            <CardTitle>
              {t(
                'Application_Specific_Attachments',
                'Application Specific Attachments',
              )}
            </CardTitle>
            {viewCandidateAttachments && showActionButtons && (
              <CardActions>
                <Button
                  children={t('Attach_a_file', 'Attach a file')}
                  size="small"
                  type="tertiary"
                  iconRightName="link"
                  onClick={() => {
                    setShowAttachFileModal(true);
                    setAttachmentType('APPLICATION');
                  }}
                />
              </CardActions>
            )}
          </CardHeader>
          <CardContent className={css.cardContent}>
            <Table
              borderRadius="12px"
              entries={
                // @ts-expect-error - TS18048 - 'attachments' is possibly 'undefined'.
                attachments.filter(
                  (attachment) => attachment.jobId === jobId,
                ) || []
              }
              // @ts-expect-error - TS2322 - Type '({ key: string; label: string; render: ({ data }: { data: any; }) => Element; sortable?: undefined; } | { key: string; label: string; render: ({ data }: { data: any; }) => Element; sortable: boolean; })[]' is not assignable to type 'GenericHeaderItems<CandidateAttachment, GenericObject>'.
              headers={attachmentTableHeader}
              stickyHeader
            />
          </CardContent>
        </Card>
      ) : null}

      <Card
        classNames={{
          wrapper: css.card,
        }}
      >
        <CardHeader>
          <CardTitle>
            {t(
              'Candidate_Specific_Attachments',
              'Candidate Specific Attachments',
            )}
          </CardTitle>
          {viewCandidateAttachments && showActionButtons && (
            <CardActions>
              <Button
                children={t('Attach_a_file', 'Attach a file')}
                size="small"
                type="tertiary"
                iconRightName="link"
                onClick={() => {
                  setShowAttachFileModal(true);
                  setAttachmentType('CANDIDATE');
                }}
              />
            </CardActions>
          )}
        </CardHeader>
        <CardContent className={css.cardContent}>
          <Table
            borderRadius="12px"
            entries={
              // @ts-expect-error - TS18048 - 'attachments' is possibly 'undefined'.
              attachments.filter((attachment) => attachment.jobId === 0) || []
            }
            // @ts-expect-error - TS2322 - Type '({ key: string; label: string; render: ({ data }: { data: any; }) => Element; sortable?: undefined; } | { key: string; label: string; render: ({ data }: { data: any; }) => Element; sortable: boolean; })[]' is not assignable to type 'GenericHeaderItems<CandidateAttachment, GenericObject>'.
            headers={attachmentTableHeader}
            stickyHeader
          />
        </CardContent>
      </Card>
      {showAttachFileModal ? (
        <AttachFile
          isOpen={showAttachFileModal}
          candidateId={applicantDetails.id}
          jobId={jobId}
          onClose={() => {
            setShowAttachFileModal(false);
            if (isEdit) {
              setEditFileName('');
              setEditAttachmentType('');
              setIsEdit(false);
            }
          }}
          title={t('Upload_Attachment', 'Upload Attachment')}
          attachmentType={attachmentType}
          initialFileName={editFileName}
          initialAttachmentType={editAttachmentType}
          onSubmit={(response) => {
            dispatch(
              updateCrmApplicantInformationData({
                ...applicantDetails,
                candidateAttachments: isEdit
                  ? // @ts-expect-error - TS18048 - 'applicantDetails.candidateAttachments' is possibly 'undefined'.
                    applicantDetails.candidateAttachments.map((attachment) =>
                      attachment.id === editFileId ? response : attachment,
                    )
                  : // @ts-expect-error - TS2488 - Type 'CandidateAttachment[] | undefined' must have a '[Symbol.iterator]()' method that returns an iterator.
                    [...applicantDetails.candidateAttachments, response],
              }),
            );
            setShowAttachFileModal(false);
            setEditFileName('');
            setEditAttachmentType('');
            setIsEdit(false);
          }}
          onDeleteFile={(deletedFileId) => {
            dispatch(
              updateCrmApplicantInformationData({
                ...applicantDetails,
                candidateAttachments:
                  // @ts-expect-error - TS18048 - 'applicantDetails.candidateAttachments' is possibly 'undefined'.
                  applicantDetails.candidateAttachments.filter(
                    (attachment) => attachment.id !== deletedFileId,
                  ),
              }),
            );
            setShowAttachFileModal(false);
            setEditFileName('');
            setEditAttachmentType('');
            setIsEdit(false);
          }}
          isEdit={isEdit}
          editFileId={editFileId}
        />
      ) : null}
    </div>
  );
};

export default ApplicantAttachments;
